import { ChangeDetectionStrategy, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { IStatistic2ReportParameter } from '@fi-app/statistic2/statistic2';
import {
    Statistic2DataRepresentationType,
    Statistic2DisplayType,
    Statistic2ObjectSelectionType,
    Statistic2ReportType,
    Statistic2ResultsPerType,
    Statistic2TimePeriod,
    Statistic2UserSelectionType,
} from '@fi-app/statistic2/statistic2-types';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Statistic2HelperService } from '@fi-app/statistic2/statistic2-helper.service';
import { AccessRightFilterCollectionModel, ILightWeightArea, ILightWeightObject } from '@fi-shared/models';
import { ModuleAccessLevel } from '@fi-shared/access-right-filters/module-access-level';
import { ILightWeightUser } from '@fi-shared/models/access-right-filter-collection.model';
import { AppTextService } from '@fi-shared/app-text';
import { ObjectTypesService } from '@fi-app/utilities/fi-services/object-types.service';
import { ObjectTypes } from '@fi-shared/types';
import { isDefined } from '@fi-shared/utils';

@Component({
    selector: 'fi-statistic2-report-parameter-edit',
    templateUrl: './statistic2-report-parameter-edit.component.html',
    styleUrls: ['./statistic2-report-parameter-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-statistic2-report-parameter-edit' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: Statistic2ReportParameterEditComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: Statistic2ReportParameterEditComponent,
        },
    ],
    standalone: false,
})
export class Statistic2ReportParameterEditComponent
    extends ControlValueAccessorValidatorBase<IStatistic2ReportParameter>
    implements OnInit
{
    @Input() currentPage: number;
    @Input() accessRightFilterCollection: AccessRightFilterCollectionModel;

    @Input() availableDocumentTags: ILightWeightObject[];
    @Input() availableDocuments: ILightWeightObject[];

    @Input() availableMediaBankFileTags: ILightWeightObject[];
    @Input() availableMediaBankFiles: ILightWeightObject[];

    protected readonly ReportType = Statistic2ReportType;
    protected readonly UserSelectionType = Statistic2UserSelectionType;
    protected readonly ObjectSelectionType = Statistic2ObjectSelectionType;
    protected readonly TimePeriods = Statistic2TimePeriod;
    protected readonly ResultsPerType = Statistic2ResultsPerType;
    protected readonly DataRepresentationType = Statistic2DataRepresentationType;
    protected readonly DisplayTypes = Statistic2DisplayType;

    protected availableAreas: ILightWeightArea[];
    protected availableBusinessUnits: ILightWeightObject[];
    protected availableRoles: ILightWeightObject[];
    protected availableUsers: ILightWeightUser[];

    // TODO: check if we can safely remove these private properties
    private selectedAreas: ILightWeightArea[];
    private selectedBusinessUnits: ILightWeightObject[];
    private selectedRoles: ILightWeightObject[];
    private selectedUsers: ILightWeightUser[];
    private selectedDocumentTags: ILightWeightObject[];
    private selectedDocuments: ILightWeightObject[];
    //

    constructor(
        private injector: Injector,
        private statistic2HelperService: Statistic2HelperService,
        private appTextService: AppTextService,
        private objectTypesService: ObjectTypesService,
    ) {
        super(injector);
    }

    public ngOnInit(): void {
        this.updateUserSelectionItems();
        this.updateReadRatingForItems();
    }

    private updateUserSelectionItems(): void {
        if (!this.showAreasSelection()) {
            this.ngModel.AreaIds = [];
        }
        if (!this.showBusinessUnitsSelection()) {
            this.ngModel.BusinessUnitIds = [];
        }
        if (!this.showUsersSelection()) {
            this.ngModel.UserIds = [];
        }
        if (!this.showRolesSelection()) {
            this.ngModel.RoleIds = [];
        }

        this.availableAreas = this.accessRightFilterCollection.AccessRightCoreFilter.Areas;
        this.selectedAreas = this.availableAreas.filter((item) => (this.ngModel.AreaIds || []).indexOf(item.Id) !== -1);

        this.availableBusinessUnits = this.accessRightFilterCollection.AccessRightCoreFilter.BusinessUnits;
        this.selectedBusinessUnits = this.availableBusinessUnits.filter(
            (item) => (this.ngModel.BusinessUnitIds || []).indexOf(item.Id) !== -1,
        );

        this.availableRoles = this.accessRightFilterCollection.AccessRightCoreFilter.Roles;
        this.selectedRoles = this.availableRoles.filter((item) => (this.ngModel.RoleIds || []).indexOf(item.Id) !== -1);

        this.availableUsers = this.accessRightFilterCollection.AccessRightUserFilter.Users;
        this.selectedUsers = this.availableUsers.filter((item) => (this.ngModel.UserIds || []).indexOf(item.Id) !== -1);
    }

    private updateReadRatingForItems(): void {
        if (!this.showTagsSelectionInput()) {
            this.ngModel.TagIds = [];
        }
        if (!this.showObjectsSelectionInput()) {
            this.ngModel.ObjectIds = [];
        }

        this.selectedDocumentTags = this.availableDocumentTags.filter(
            (item) => (this.ngModel.TagIds || []).indexOf(item.Id) !== -1,
        );
        this.selectedDocuments = this.availableDocuments.filter(
            (item) => (this.ngModel.ObjectIds || []).indexOf(item.Id) !== -1,
        );
    }

    protected showObjectSelection(): boolean {
        return (
            this.ngModel.ReportType === Statistic2ReportType.ReadRating ||
            this.ngModel.ReportType === Statistic2ReportType.FileViews
        );
    }

    protected showTimePeriod(): boolean {
        return (
            this.ngModel.ReportType ===
            Statistic2ReportType.UserLogins /* || this.ngModel.ReportType === Statistic2ReportType.FileViews*/
        );
    }

    protected isCustomTimePeriod(): boolean {
        return this.ngModel.TimePeriod === Statistic2TimePeriod.Custom;
    }

    protected showUserSelectionAreas(): boolean {
        return this.accessRightFilterCollection.AccessLevel === ModuleAccessLevel.Global;
    }

    protected showUserSelectionUnits(): boolean {
        return (
            this.accessRightFilterCollection.AccessLevel === ModuleAccessLevel.Global ||
            this.accessRightFilterCollection.AccessLevel === ModuleAccessLevel.Central
        );
    }

    protected showPieChartDisplayType(): boolean {
        return this.statistic2HelperService.showPieChartDisplayType(this.ngModel);
    }

    protected onUserSelectionTypeChanged(): void {
        this.updateUserSelectionItems();
        this.doPropagateChanges();
    }

    protected onReportTypeChanged(): void {
        this.ngModel.TagIds = [];
        this.ngModel.ObjectIds = [];
        this.doPropagateChanges();
    }

    protected onObjectSelectionTypeChanged(): void {
        this.ngModel.TagIds = [];
        this.ngModel.ObjectIds = [];
        this.updateReadRatingForItems();
        this.doPropagateChanges();
    }

    protected onTimePeriodChanged(): void {
        if (this.isCustomTimePeriod()) {
            const date = new Date();
            date.setHours(0, 0, 0, 0);
            this.ngModel.TimePeriodEndDate = new Date(date.getTime());
            date.setDate(date.getDate() - 7);
            this.ngModel.TimePeriodStartDate = new Date(date.getTime());
        } else {
            this.ngModel.TimePeriodStartDate = null;
            this.ngModel.TimePeriodEndDate = null;
        }
        this.doPropagateChanges();
    }

    public doPropagateChanges(): void {
        // setTimeout - for proper validation
        setTimeout(() => super.doPropagateChanges());
    }

    public getDefaultReportName(): string {
        const now = new Date();
        const dateStr =
            now.getFullYear() + '-' + ('0' + (now.getMonth() + 1)).slice(-2) + '-' + ('0' + now.getDate()).slice(-2);
        if (this.ngModel.ReportType === Statistic2ReportType.ReadRating) {
            return 'Read rating report ' + dateStr;
        }
        if (this.ngModel.ReportType === Statistic2ReportType.UserLogins) {
            return 'User logins report ' + dateStr;
        }
        if (this.ngModel.ReportType === Statistic2ReportType.FileViews) {
            return 'File views report ' + dateStr;
        }
    }

    protected getReportTypeText(reportType: Statistic2ReportType): string {
        return this.statistic2HelperService.getReportTypeText(reportType);
    }

    protected getObjectSelectionText(
        objectSelectionType: Statistic2ObjectSelectionType,
        reportType: Statistic2ReportType,
    ): string {
        return this.statistic2HelperService.getObjectsSelectionText(objectSelectionType, reportType);
    }

    protected getUserSelectionText(userSelectionType: Statistic2UserSelectionType): string {
        return this.statistic2HelperService.getUserSelectionText(userSelectionType);
    }

    protected getShowResultsPerText(resultsPerType: Statistic2ResultsPerType): string {
        return this.statistic2HelperService.getShowResultsPerText(resultsPerType);
    }

    protected getTimePeriodText(timePeriod: Statistic2TimePeriod): string {
        return this.statistic2HelperService.getTimePeriodText(timePeriod);
    }

    protected showAreasSelection(): boolean {
        return this.ngModel.UserSelectionType === Statistic2UserSelectionType.Areas;
    }
    protected showBusinessUnitsSelection(): boolean {
        return this.ngModel.UserSelectionType === Statistic2UserSelectionType.BusinessUnits;
    }
    protected showUsersSelection(): boolean {
        return this.ngModel.UserSelectionType === Statistic2UserSelectionType.Users;
    }
    protected showRolesSelection(): boolean {
        return this.ngModel.UserSelectionType === Statistic2UserSelectionType.Roles;
    }
    protected getUserSelectionDescriptionText(): string {
        let text = this.appTextService.get('select') + ' ';
        if (this.showAreasSelection())
            text += this.objectTypesService.GetNameByObjectType(ObjectTypes.Area, true).toLowerCase();
        else if (this.showBusinessUnitsSelection())
            text += this.objectTypesService.GetNameByObjectType(ObjectTypes.BusinessUnit, true).toLowerCase();
        else if (this.showUsersSelection())
            text += this.objectTypesService.GetNameByObjectType(ObjectTypes.User, true).toLowerCase();
        else if (this.showRolesSelection())
            text += this.objectTypesService.GetNameByObjectType(ObjectTypes.Role, true).toLowerCase();
        return text;
    }

    protected showTagsSelectionInput(): boolean {
        return (
            this.showObjectSelection() &&
            this.ngModel.ObjectSelectionType === Statistic2ObjectSelectionType.SelectedTags
        );
    }
    protected showObjectsSelectionInput(): boolean {
        return (
            this.showObjectSelection() &&
            this.ngModel.ObjectSelectionType === Statistic2ObjectSelectionType.SelectedItems
        );
    }
    protected getObjectsSelectionDescriptionText(): string {
        let text = this.appTextService.get('select') + ' ';
        if (this.showTagsSelectionInput()) {
            text += this.objectTypesService.GetNameByObjectType(ObjectTypes.Tag, true).toLowerCase();
        } else if (this.showObjectsSelectionInput()) {
            let objectType: ObjectTypes;
            if (this.ngModel.ReportType === Statistic2ReportType.ReadRating) {
                objectType = ObjectTypes.Document;
            } else if (this.ngModel.ReportType === Statistic2ReportType.FileViews) {
                objectType = ObjectTypes.MediaBank;
            }
            if (isDefined(objectType)) {
                text += this.objectTypesService.GetNameByObjectType(objectType, true).toLowerCase();
            }
        }
        return text;
    }

    protected getTagsForSelection(reportType: Statistic2ReportType): ILightWeightObject[] {
        if (reportType === Statistic2ReportType.ReadRating) {
            return this.availableDocumentTags;
        }
        if (reportType === Statistic2ReportType.FileViews) {
            return this.availableMediaBankFileTags;
        }
        throw new Error('getTagsForSelection, wrong reportType: ' + reportType);
    }
    protected getObjectsForSelection(reportType: Statistic2ReportType): ILightWeightObject[] {
        if (reportType === Statistic2ReportType.ReadRating) {
            return this.availableDocuments;
        }
        if (reportType === Statistic2ReportType.FileViews) {
            return this.availableMediaBankFiles;
        }
        throw new Error('getObjectsForSelection, wrong reportType: ' + reportType);
    }

    protected isShowDataPresentationType(): boolean {
        return this.statistic2HelperService.isShowDataPresentationType(this.ngModel);
    }

    protected onSelectionChange(): void {
        this.doPropagateChanges();
    }
}
